<template>
	<div
		v-if="grades && grades.length > 0"
		class="storage-selector"
	>
		<div v-if="grades.length > 1">
			<TnParagraph
				class="color-text"
				:size="content?.headingSize"
				bold
				responsive
			>
				{{ content.heading || "Grading" }}
			</TnParagraph>
			<ECommerceStorageSelectorItem
				v-for="(grade, idx) in grades"
				:key="`grade-${idx}`"
				class="item"
				:class="{ disabled: grades && grades.length == 1 }"
				@click="conditionSelected(grade)"
				v-model="conditionFromPath"
				:to="grade.href"
				:disabled="isDisabled(grade)"
				:active="eCommerceProduct.condition === grade.condition"
				>{{ `${grade.condition.length > 0 ? grade.condition.slice(1) : ""}-grade` }}
			</ECommerceStorageSelectorItem>
		</div>
		<div v-if="grades && grades.length === 1 && grades[0].condition">
			<TnParagraph
				class="color-text"
				:size="content?.headingSize"
				bold
				responsive
			>
				{{ content.heading || "Grading" }}
			</TnParagraph>

			<ECommerceStorageSelectorItem
				v-for="(grade, idx) in grades"
				:key="`grade-${idx}`"
				class="item"
				active
				>{{ `${grade.condition.slice(1)}-grade` }}
			</ECommerceStorageSelectorItem>
		</div>
	</div>
</template>

<script>
import GizmoComponentMixin from "~/mixins/platform/GizmoComponentMixin.js";
import ECommerceProductMixin from "~/components/ecommerce/ECommerceProductMixin/ECommerceProductMixin.js";

import { mapState } from "vuex";
import urlSafeECommerceTitle from "~/helpers/ecommerce/urlSafeECommerceTitle";
import safeEcommerceUrl from "~/helpers/ecommerce/safeEcommerceUrl";
import { mapActions } from "pinia";
import { usePageStore } from "~/pinia/platform/page.ts";

export default defineNuxtComponent({
	name: "ECommerceGradingSelector",
	mixins: [GizmoComponentMixin, ECommerceProductMixin],

	computed: {
		...mapState("ecommerce", ["product"]),
		variants() {
			if (this.eCommerceProduct?.variants?.length > 0)
				return this.eCommerceProduct?.variants.filter((v) => v.condition);
			else return [this.eCommerceProduct];
		},

		links() {
			const path = useRoute().fullPath;
			return this.grades.map((variant) => urlSafeECommerceTitle(path, variant.title));
		},
		grades() {
			if (this.eCommerceProduct?.variants?.length > 0) {
				const variantsCopy = JSON.parse(JSON.stringify(this.eCommerceProduct?.variants));

				variantsCopy.sort((a, b) => {
					if (a.condition > b.condition) return 1;
					else if (a.condition < b.condition) return -1;
					return 0;
				});

				const uniqueVariants = [];

				variantsCopy.forEach((vc) => {
					if (uniqueVariants.find((uv) => uv.condition === vc.condition))
						// This grading has already been resolved
						return;
					const matchingVariants = variantsCopy.filter((v) => v.condition === vc.condition);
					const memoryMatch = matchingVariants.find(
						(mv) => mv.memory === this.eCommerceProduct.memory && mv.colorCode === this.eCommerceProduct.colorCode,
					);
					if (memoryMatch) uniqueVariants.push(memoryMatch);
					else uniqueVariants.push(matchingVariants[0]);
				});

				return uniqueVariants.map((variant) => {
					return {
						...variant,
						href: safeEcommerceUrl(
							this.eCommerceProduct?.category,
							this.eCommerceProduct?.brand,
							variant.title,
							variant.condition,
						),
					};
				});
			} else if (this.eCommerceProduct?.condition) {
				return [this.eCommerceProduct];
			} else {
				return [];
			}
		},
		conditionFromPath() {
			return this.eCommerceProduct?.condition;
		},
	},

	methods: {
		...mapActions(usePageStore, ["loadPage"]),
		isDisabled(grade) {
			if (!this.checkIfSizeAndColorHasGrade(grade)) {
				return true;
			}

			return !grade?.inventory?.[0]?.amount;
		},
		checkIfSizeAndColorHasGrade(grade) {
			const variantsInGrading = this.eCommerceProduct.variants?.filter(
				(variant) => variant.condition === grade.condition,
			);
			const variantsInSize = variantsInGrading?.filter((variant) => variant.memory === grade.memory);
			return variantsInSize?.some((variant) => variant.color === this.eCommerceProduct.color);
		},
		async conditionSelected(grade) {
			this.$store.commit("ecommerce/setProduct", {
				...this.$store.state.ecommerce.product,
				...grade,
				loading: true,
			}); // Fake update to make UI react instantly

			if (import.meta.client) {
				history.replaceState(null, "", grade.href + window.location.hash);
			}

      useAnalytics().page.loaded();

			await this.loadPage(grade.href);
		},
	},
});
</script>

<style lang="scss" scoped>
.item {
	display: inline-block;
	margin-right: $spacing-s;
	margin-bottom: $spacing-s;
}

.disabled {
	pointer-events: none;
	cursor: pointer;
}

.color-text {
	margin-bottom: $spacing-m;
	color: $color-neutrals-900-shade;
}
</style>
